import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { contactService, ContactMessage } from "../../api/contactService";

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #333;
  margin: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case "new":
        return "#e3f2fd";
      case "read":
        return "#fff3e0";
      case "replied":
        return "#e8f5e8";
      default:
        return "#f5f5f5";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "new":
        return "#1976d2";
      case "read":
        return "#f57c00";
      case "replied":
        return "#388e3c";
      default:
        return "#666";
    }
  }};
`;

const MessageCard = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const MessageInfo = styled.div`
  flex: 1;
`;

const MessageName = styled.h3`
  font-size: 1.1rem;
  color: #333;
  margin: 0 0 0.25rem 0;
`;

const MessageEmail = styled.p`
  color: #666;
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
`;

const MessageDate = styled.p`
  color: #999;
  margin: 0;
  font-size: 0.8rem;
`;

const MessageContent = styled.p`
  color: #444;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  white-space: pre-wrap;
`;

const MessageActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{
  variant?: "primary" | "danger" | "secondary";
}>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: #e97d7d;
          color: white;
          &:hover {
            background: #d96a6a;
          }
        `;
      case "danger":
        return `
          background: #e74c3c;
          color: white;
          &:hover {
            background: #c0392b;
          }
        `;
      case "secondary":
        return `
          background: #f8f9fa;
          color: #666;
          border: 1px solid #dee2e6;
          &:hover {
            background: #e9ecef;
          }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
          &:hover {
            background: #5a6268;
          }
        `;
    }
  }}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e74c3c;
  background: #fdf2f2;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  background: #f8f9fa;
  border-radius: 0.5rem;
`;

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ContactMessages: React.FC = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await contactService.getAllMessages();
      setMessages(data);
    } catch (err) {
      console.error("Error fetching contact messages:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleStatusUpdate = async (
    id: string,
    status: "new" | "read" | "replied"
  ) => {
    try {
      await contactService.updateMessageStatus(id, status);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === id
            ? { ...msg, status, updatedAt: new Date().toISOString() }
            : msg
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update status");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    try {
      await contactService.deleteMessage(id);
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete message");
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading contact messages...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <ActionButton onClick={fetchMessages}>Retry</ActionButton>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Contact Messages</Title>
        <ActionButton onClick={fetchMessages}>Refresh</ActionButton>
      </Header>

      {messages.length === 0 ? (
        <EmptyMessage>No contact messages found.</EmptyMessage>
      ) : (
        messages.map((message) => (
          <MessageCard key={message.id}>
            <MessageHeader>
              <MessageInfo>
                <MessageName>{message.name}</MessageName>
                <MessageEmail>{message.email}</MessageEmail>
                <MessageDate>{formatDate(message.createdAt)}</MessageDate>
              </MessageInfo>
              <StatusBadge status={message.status}>
                {message.status}
              </StatusBadge>
            </MessageHeader>

            <MessageContent>{message.message}</MessageContent>

            <MessageActions>
              {message.status === "new" && (
                <ActionButton
                  variant="primary"
                  onClick={() => handleStatusUpdate(message.id, "read")}
                >
                  Mark as Read
                </ActionButton>
              )}
              {message.status === "read" && (
                <ActionButton
                  variant="secondary"
                  onClick={() => handleStatusUpdate(message.id, "replied")}
                >
                  Mark as Replied
                </ActionButton>
              )}
              <ActionButton
                variant="danger"
                onClick={() => handleDelete(message.id)}
              >
                Delete
              </ActionButton>
            </MessageActions>
          </MessageCard>
        ))
      )}
    </Container>
  );
};

export default ContactMessages;
